import { type Breakpoint } from '@divi/types';

import {
  type MultiViewDataItemAttrs,
  type MultiViewDataUpdateAttrs,
  type MultiViewUpdateElementParams,
  type PseudoStateDataMap,
  type RuntimePseudoEvent,
  type RuntimePseudoState,
} from '../../types';
import { createSwitchDataToCurrentPseudoState } from '../create-switch-data-to-current-pseudo-state';
import { dispatchCustomEvent } from '../dispatch-custom-event';
import { hasPseudoStateData } from '../has-pseudo-state-data';
import { parseData } from '../parse-data';
import { registerPseudoStateListeners } from '../register-pseudo-state-listeners';

/**
 * Set classes to to hide image when the src is empty, otherwise it will set classes
 * to show a loading transition.
 *
 * @since ??
 *
 * @param {string} src A string representing the source URL of an image.
 * @param {HTMLImageElement} element The image element target.
 */
const setImageHiddenClass = (src: string, element: HTMLImageElement) => {
  if (src) {
    element.classList.remove('et_multi_view_hidden_image');

    // Set class to show loading transition.
    element.classList.add('et_multi_view_image__loading');
    element.classList.remove('et_multi_view_image__loaded');

    element.addEventListener(
      'load',
      () => {
        element.classList.remove('et_multi_view_image__loading');
        element.classList.add('et_multi_view_image__loaded');
      },
      { once: true },
    );
  } else {
    element.classList.add('et_multi_view_hidden_image');
  }
};

/**
 * Sets the source of the lightbox image.
 *
 * @since ??
 *
 * @param {string} src The source URL of the image.
 */
const setLightboxImage = (src: string) => {
  const imageTarget = document.querySelector<HTMLImageElement>('img.mfp-img');

  if (imageTarget) {
    imageTarget.src = src;
  }
};

/**
 * Sets the attributes of an element and dispatches a custom event.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to set the attributes of an element.
 * @param {MultiViewDataItemAttrs} params.stateData The data to set as the attributes.
 * @param {MultiViewDataUpdateAttrs} params.item The data item being updated.
 * @param {HTMLElement} params.element The element to update.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint.
 * @param {string} params.state The state of the element ('default' or 'hover').
 * @param {MouseEvent | TouchEvent} params.originalEvent The original event that triggered the update.
 */
const setElementAttrs = ({
  stateData,
  item,
  element,
  breakpoint,
  state,
  originalEvent,
}: MultiViewUpdateElementParams<MultiViewDataItemAttrs>) => {
  const updatedData: MultiViewDataItemAttrs = {};
  let updated = false;

  Object.keys(stateData).forEach(key => {
    const currentAttrValue = element.getAttribute(key) ?? '';
    const updateAttrValue = stateData[key];

    if (updateAttrValue !== currentAttrValue) {
      if ('src' === key && element instanceof HTMLImageElement) {
        setImageHiddenClass(updateAttrValue, element);
      }

      if (
        'href' === key &&
        element instanceof HTMLAnchorElement &&
        element.classList.contains('et_pb_lightbox_image--open')
      ) {
        setLightboxImage(updateAttrValue);
      }

      element.setAttribute(key, updateAttrValue);

      updated = true;
      updatedData[key] = updateAttrValue;
    }
  });

  if (updated) {
    // Dispatch custom event: divi:multiView:setAttrs
    dispatchCustomEvent<MultiViewDataItemAttrs>({
      breakpoint,
      item,
      originalEvent,
      state,
      updatedData,
    });
  }
};

/**
 * Update element attributes.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to update the attributes of an element.
 * @param {MultiViewDataUpdateAttrs} params.item The item that will be updated.
 * @param {string} params.listenerNamespace Unique namespace for pseudo-state listener cleanup.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The list of breakpoint names.
 */
export const updateDataAttrs = ({
  item,
  listenerNamespace = 'update-data-attrs',
  breakpoint,
  isInit = false,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  item: MultiViewDataUpdateAttrs;
  listenerNamespace?: string;
  breakpoint: Breakpoint.Name;
  isInit: boolean;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): void => {
  const { element, hoverElements, data } = item;
  const { defaultStateData, hoverStateData, focusStateData, checkedStateData, activeStateData } =
    parseData<MultiViewDataItemAttrs>({
      data,
      breakpoint,
      baseBreakpoint,
      breakpointNames,
    });

  // Bail early default state data is null.
  // This can happen if the data is not valid.
  if (null === defaultStateData) {
    return;
  }

  const setStateData = (stateData: MultiViewDataItemAttrs, state: RuntimePseudoState, event?: RuntimePseudoEvent) => {
    setElementAttrs({
      stateData,
      state,
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  const stateDataMap: PseudoStateDataMap<MultiViewDataItemAttrs> = {
    defaultStateData,
    hoverStateData,
    focusStateData,
    checkedStateData,
    activeStateData,
  };

  const switchDataToDefaultState = (event?: RuntimePseudoEvent) => {
    setStateData(defaultStateData, 'default', event);
  };

  const switchDataToCurrentPseudoState = createSwitchDataToCurrentPseudoState({
    stateDataMap,
    setStateData,
  });

  // Handle pseudo states.
  if (hasPseudoStateData(stateDataMap)) {
    registerPseudoStateListeners({
      element,
      hoverElements,
      stateDataMap,
      onPseudoStateChange: switchDataToCurrentPseudoState,
      listenerNamespace,
    });
  }

  // Switch to default state on page load or when the breakpoint changes that triggered when the user
  // resizing the browser or changing the device orientation.
  if ('desktop' === breakpoint && isInit && !item.switchOnLoad) {
    return;
  }

  switchDataToDefaultState();
};
